import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import QRCode from "qrcode";
import barlowRegularUrl from "../../../../../assets/fonts/cpe/BarlowCondensed-Regular.ttf";
import barlowSemiBoldUrl from "../../../../../assets/fonts/cpe/BarlowCondensed-SemiBold.ttf";
import barlowBoldUrl from "../../../../../assets/fonts/cpe/BarlowCondensed-Bold.ttf";

const W = 226.77;
const H = 610;
const M = 12;
const CW = W - (M * 2);
// Aire que se deja bajo el ultimo texto al recortar el papel en blanco del final.
// Antes ese recorte lo hacia el modal con un 160pt fijo, que se llevaba
// TERMINOS Y CONDICIONES, GRACIAS POR CONFIAR, HORA LLEGADA y REGISTRADO POR.
const TICKET_TRAILING_MARGIN = 10;
const INK = rgb(0.03, 0.035, 0.045);
const MUTED = rgb(0.34, 0.35, 0.37);
const LINE = rgb(0.7, 0.71, 0.73);
const LIGHT_LINE = rgb(0.82, 0.83, 0.85);
const ALERT = rgb(0.82, 0.12, 0.12);
const ICON_MUTED = rgb(0.48, 0.5, 0.53);
const WHITE = rgb(1, 1, 1);

const clean = (value) => String(value || "").replace(/\s+/g, " ").trim();

const ticketPayloadDesdeFormulario = ({ encomienda = {}, empresa = {} }) => ({
  rubro: "TRANS_ENCOMIENDA",
  endpoint_pdf: "/cpesunatticketencomienda/v2",
  empresa: {
    ruc: empresa.ruc || empresa.documento_id || encomienda.documento_id || "",
    razon_social: empresa.razon_social || empresa.nombre || empresa.nombre_comercial || "TRANSPORTE DE ENCOMIENDAS",
    nombre_comercial: empresa.nombre_comercial || empresa.razon_social || empresa.nombre || "TRANSPORTE DE ENCOMIENDAS",
    domicilio_fiscal: empresa.domicilio_fiscal || empresa.direccion || "",
    direccion: empresa.direccion || empresa.domicilio_fiscal || "",
  },
  cliente: {
    razon_social_nombres: encomienda.cliente || "",
    documento_identidad: encomienda.cliente_documento || encomienda.cliente_documento_id || "",
    tipo_identidad: encomienda.cliente_id_doc || encomienda.id_documento || "",
    cliente_direccion: encomienda.cliente_direccion_fact || encomienda.cliente_direccion || encomienda.remitente_direccion || "",
  },
  venta: {
    codigo: encomienda.r_cod_ref || encomienda.r_cod || "03",
    serie: encomienda.r_serie_ref || encomienda.r_serie || "",
    numero: encomienda.r_numero_ref || encomienda.r_numero || "",
    fecha_emision: encomienda.r_fecemi || "",
    hora_emision: encomienda.ctrl_crea_hora || encomienda.ctrl_crea || encomienda.hora_grabacion || "",
    forma_pago_id: encomienda.condicion_pago || "PAGADO",
    total: encomienda.r_monto_total || encomienda.precio_neto || 0,
    r_monto_total: encomienda.r_monto_total || encomienda.precio_neto || 0,
  },
  encomienda: {
    ...encomienda,
    r_fecemi: encomienda.r_fecemi || "",
    registrado_por_correo: encomienda.registrado_por_correo || encomienda.ctrl_crea_us || "",
  },
  ticket: {
    formato: "ENCOMIENDA_BOARDING_PASS_80MM",
    titulo: "ENCOMIENDA",
    mostrar_origen_destino: true,
    mostrar_qr: true,
    registrado_por_correo: encomienda.registrado_por_correo || encomienda.ctrl_crea_us || "",
    endpoint_pdf: "/cpesunatticketencomienda/v2",
    rubro: "TRANS_ENCOMIENDA",
  },
  items: [
    {
      producto: encomienda.descripcion || "SERVICIO DE TRANSPORTE DE ENCOMIENDA",
      cantidad: 1,
      precio_neto: encomienda.r_monto_total || encomienda.precio_neto || 0,
    },
  ],
});

const money = (value) => Number(value || 0).toLocaleString("es-PE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const datePe = (value) => {
  const text = String(value || "").slice(0, 10);
  return text ? text.split("-").reverse().join("/") : "-";
};

const timePe = (value) => {
  const textValue = clean(value);
  if (!textValue) return "-";
  const time = textValue.includes("T") ? textValue.split("T")[1] : textValue.split(" ")[1] || textValue;
  const [hour = "0", minute = "00"] = time.split(".")[0].split(":");
  const hourNumber = Number(hour);
  if (!Number.isFinite(hourNumber)) return textValue;
  const suffix = hourNumber >= 12 ? "PM" : "AM";
  const hour12 = hourNumber % 12 || 12;
  return `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${suffix}`;
};

const documentName = (code) => (code === "01" ? "FACTURA ELECTRONICA" : "BOLETA ELECTRONICA");

const base64ToBytes = (base64) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const fit = (value, font, size, maxWidth) => {
  const textValue = clean(value);
  if (!textValue) return "";
  if (font.widthOfTextAtSize(textValue, size) <= maxWidth) return textValue;

  let output = textValue;
  while (output.length > 3 && font.widthOfTextAtSize(`${output}...`, size) > maxWidth) {
    output = output.slice(0, -1);
  }
  return output.length > 3 ? `${output}...` : "";
};

const wrap = (value, font, size, maxWidth, maxLines = 2) => {
  const words = clean(value).split(" ").filter(Boolean);
  const lines = [];
  let lineValue = "";

  for (const word of words) {
    const next = lineValue ? `${lineValue} ${word}` : word;
    if (font.widthOfTextAtSize(next, size) <= maxWidth) {
      lineValue = next;
      continue;
    }
    if (lineValue) lines.push(lineValue);
    lineValue = word;
    if (lines.length >= maxLines) break;
  }

  if (lineValue && lines.length < maxLines) lines.push(lineValue);
  if (!lines.length) return [""];
  lines[lines.length - 1] = fit(lines[lines.length - 1], font, size, maxWidth);
  return lines;
};

const wrapPreservingBreaks = (value, font, size, maxWidth, maxLines = 8) => {
  const sourceLines = String(value || "").replace(/\r\n/g, "\n").split("\n");
  const lines = [];

  for (const sourceLine of sourceLines) {
    if (lines.length >= maxLines) break;
    lines.push(...wrap(sourceLine, font, size, maxWidth, maxLines - lines.length));
  }

  return lines.slice(0, maxLines);
};

const text = (page, value, x, y, size, font, color = INK, maxWidth = null) => {
  page.drawText(maxWidth ? fit(value, font, size, maxWidth) : clean(value), { x, y, size, font, color });
};

const centered = (page, value, y, size, font, color = INK, maxWidth = CW) => {
  const label = fit(value, font, size, maxWidth);
  const width = font.widthOfTextAtSize(label, size);
  page.drawText(label, { x: (W - width) / 2, y, size, font, color });
};

const right = (page, value, y, size, font, color = INK, rightX = W - M, maxWidth = CW) => {
  const label = fit(value, font, size, maxWidth);
  const width = font.widthOfTextAtSize(label, size);
  page.drawText(label, { x: rightX - width, y, size, font, color });
};

const dotted = (page, y, x1 = M, x2 = W - M) => {
  for (let x = x1; x < x2; x += 5) {
    page.drawCircle({ x, y, size: 0.65, color: LINE });
  }
};

const box = (page, x, y, width, height, fill = WHITE, border = LINE, borderWidth = 0.55) => {
  page.drawRectangle({ x, y, width, height, color: fill, borderColor: border, borderWidth });
};

const drawIcon = (page, pathData, x, y, size = 12, color = ICON_MUTED) => {
  page.drawSvgPath(pathData, { x, y, scale: size / 24, color });
};

const ICONS = {
  place: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
  package: "M20 8.69V18c0 .72-.38 1.38-1 1.73l-6 3.46c-.62.36-1.38.36-2 0l-6-3.46A2 2 0 0 1 4 18V8.69c0-.72.38-1.38 1-1.73l6-3.46c.62-.36 1.38-.36 2 0l6 3.46c.62.35 1 1.01 1 1.73zM12 5.23 6.74 8.26 12 11.29l5.26-3.03L12 5.23zm-6 4.76V18l5 2.88v-7.86L6 9.99zm12 0-5 3.03v7.86L18 18V9.99z",
};

const centeredIn = (page, value, x, y, width, size, font, color = INK) => {
  const label = fit(value, font, size, width - 4);
  const textWidth = font.widthOfTextAtSize(label, size);
  page.drawText(label, { x: x + ((width - textWidth) / 2), y, size, font, color });
};

const mixedLine = (page, parts, x, y, maxRight = W - M) => {
  const cleanedParts = parts
    .map((part) => ({ ...part, value: clean(part.value) }))
    .filter((part) => part.value);
  let cursor = x;

  cleanedParts.forEach((part, index) => {
    if (index > 0) cursor += part.gap || 0;
    const availableWidth = Math.max(10, maxRight - cursor);
    const label = fit(part.value, part.font, part.size, availableWidth);
    page.drawText(label, { x: cursor, y, size: part.size, font: part.font, color: part.color || INK });
    cursor += part.font.widthOfTextAtSize(label, part.size);
  });
};

const drawPaymentStatusLarge = (page, { isPending, label, x, y, width, bold, semibold, scale = 1 }) => {
  if (isPending) {
    centeredIn(page, "POR", x, y, width, 19.2 * scale, bold, ALERT);
    centeredIn(page, "PAGAR", x, y - (22 * scale), width, 19.2 * scale, bold, ALERT);
    return;
  }

  centeredIn(page, label, x, y - (5 * scale), width, 10.5 * scale, semibold, MUTED);
};

const fetchFontBytes = async (url) => {
  const response = await fetch(url);
  return response.arrayBuffer();
};

const embedTicketFonts = async (pdfDoc) => {
  pdfDoc.registerFontkit(fontkit);

  try {
    const [regularBytes, semiboldBytes, boldBytes] = await Promise.all([
      fetchFontBytes(barlowRegularUrl),
      fetchFontBytes(barlowSemiBoldUrl),
      fetchFontBytes(barlowBoldUrl),
    ]);

    const regular = await pdfDoc.embedFont(regularBytes);
    const semibold = await pdfDoc.embedFont(semiboldBytes);
    const bold = await pdfDoc.embedFont(boldBytes);

    return { regular, semibold, bold };
  } catch (error) {
    const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const semibold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    return { regular, semibold, bold };
  }
};

const generarPdfTicketEncomiendaTributario = async (jsonTicket) => {
  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([W, H]);
  const { regular, semibold, bold } = await embedTicketFonts(pdfDoc);

  const empresa = jsonTicket.empresa || {};
  const venta = jsonTicket.venta || {};
  const encomienda = jsonTicket.encomienda || {};
  const cliente = jsonTicket.cliente || {};
  const code = venta.codigo || encomienda.r_cod || "03";
  const serie = venta.serie || encomienda.r_serie || "";
  const number = venta.numero || encomienda.r_numero || "";
  const fullNumber = [code, serie, number].filter(Boolean).join("-");
  const displayNumber = [serie, number].filter(Boolean).join("-") || fullNumber;
  const issueDate = venta.fecha_emision || encomienda.r_fecemi;
  const issueTime = venta.hora_emision || encomienda.ctrl_crea || encomienda.hora_grabacion;
  const total = venta.total || venta.r_monto_total || encomienda.r_monto_total || encomienda.precio_neto;
  const origin = encomienda.punto_venta_nombre || encomienda.id_punto_venta || "ORIGEN";
  const destination = encomienda.punto_venta_dest_nombre || encomienda.punto_venta_destino_nombre || encomienda.destino_nombre || encomienda.id_punto_venta_dest || "DESTINO";
  const senderName = encomienda.cliente || cliente.razon_social_nombres || "-";
  const senderDoc = encomienda.cliente_documento || encomienda.cliente_documento_id || cliente.documento_identidad || "-";
  const senderOriginZone = clean(encomienda.remitente_zona || encomienda.cliente_zona);
  const isInvoice = code === "01";
  const senderBillingAddress = clean(encomienda.cliente_direccion_fact || cliente.cliente_direccion_fact || cliente.direccion || "");
  const senderPickupAddressRaw = clean(encomienda.remitente_direccion || (!isInvoice ? encomienda.cliente_direccion || cliente.cliente_direccion : ""));
  const senderPickupAddress = isInvoice && senderPickupAddressRaw === senderBillingAddress ? "" : senderPickupAddressRaw;
  const receiverName = encomienda.destinatario || "-";
  const receiverDoc = encomienda.destinatario_documento || encomienda.destinatario_documento_id || "-";
  const receiverArrivalZone = clean(encomienda.destinatario_zona);
  const receiverAddress = clean(encomienda.destinatario_direccion);
  const payment = clean(encomienda.condicion_pago || venta.forma_pago_id || "PAGADO").toUpperCase();
  const paymentNormalized = payment.replace(/[^A-Z]/g, "");
  const isPaymentPending = paymentNormalized.includes("PORCOBRAR") || paymentNormalized.includes("PORPAGAR");
  const paymentLabel = isPaymentPending ? "POR PAGAR" : "CANCELADO";
  const arrivalApprox = timePe(encomienda.llegada_aprox || venta.llegada_aprox || jsonTicket.llegada_aprox);
  const registeredBy = clean(
    encomienda.registrado_por_correo ||
    encomienda.ctrl_crea_us ||
    jsonTicket.ticket?.registrado_por_correo ||
    venta.registrado_por_correo ||
    "-"
  );
  const content = encomienda.descripcion || jsonTicket.items?.[0]?.producto || "SERVICIO DE TRANSPORTE DE ENCOMIENDA";

  const originBlockFontSize = 8.7;
  const originBlockDataFontSize = 9.8;
  const originOptionalLineHeight = 11.2;
  const senderZoneLines = senderOriginZone ? wrap(senderOriginZone, regular, originBlockDataFontSize, CW - 73, 2) : [];
  const senderBillingAddressLines = isInvoice && senderBillingAddress
    ? wrap(senderBillingAddress, regular, originBlockDataFontSize, CW - 73, 2)
    : [];
  const senderPickupAddressLines = senderPickupAddress ? wrap(senderPickupAddress, regular, originBlockDataFontSize, CW - 73, 2) : [];
  const originOptionalLines = senderZoneLines.length + senderBillingAddressLines.length + senderPickupAddressLines.length;
  const originOptionalStartY = 405.7;

  const destinationOptionalLineHeight = 12.4;
  const receiverZoneLines = receiverArrivalZone ? wrap(receiverArrivalZone, regular, originBlockDataFontSize, CW - 73, 2) : [];
  const receiverAddressLines = receiverAddress ? wrap(receiverAddress, regular, originBlockDataFontSize, CW - 73, 2) : [];
  const destinationOptionalLines = receiverZoneLines.length + receiverAddressLines.length;
  const destinationOptionalStartY = 306.7;
  const destinationBaseY = destinationOptionalLines
    ? destinationOptionalStartY - ((destinationOptionalLines - 1) * destinationOptionalLineHeight) - 8
    : 313;

  const descriptionFontSize = 11.4;
  const descriptionLineHeight = 11.2;
  const descriptionLines = wrapPreservingBreaks(String(content).toUpperCase(), regular, descriptionFontSize, CW - 16, 8);
  const descriptionLineCount = Math.max(1, descriptionLines.length);
  const encomiendaTopY = 264;
  const encomiendaBaseY = 232 - ((descriptionLineCount - 1) * descriptionLineHeight) - 3;
  const dynamicSummaryShift = encomiendaBaseY - 149;
  const qrText = [empresa.ruc, code, serie, number, issueDate, senderDoc, total].map(clean).join("|");
  const qrDataUrl = await QRCode.toDataURL(qrText || displayNumber || empresa.ruc || "XPERTCONT");
  const qrImage = await pdfDoc.embedPng(base64ToBytes(qrDataUrl.split(",")[1]));

  // Desplazamiento vertical de TODO el cuerpo del ticket (razon social, RUC,
  // origen, destino, encomienda, totales, terminos y hora de llegada).
  //
  // Todos los y del ticket se derivan de esta constante a traves de bodyY(),
  // asi que moverla sube el bloque completo y no descuadra nada entre si.
  // Sin logo ni titulo, el cuerpo sube en bloque para ocupar la cabecera y que
  // el recorte inferior quite mas papel blanco sin descuadrar las secciones.
  const BODY_Y_OFFSET = 20;
  const bodyY = (value) => value + BODY_Y_OFFSET;
  const HEADER_HEIGHT_REDUCTION = 10;
  const afterHeaderY = (value) => bodyY(value + HEADER_HEIGHT_REDUCTION);
  const originLastDataY = originOptionalLines
    ? originOptionalStartY - ((originOptionalLines - 1) * originOptionalLineHeight) - 0.4
    : 418.2;
  const destinationAfterOriginBaseY = originLastDataY - 8;
  const ORIGIN_HEIGHT_REDUCTION = destinationAfterOriginBaseY - 353;
  const ORIGIN_TO_DATE_SHIFT = 34;
  const originY = (value) => afterHeaderY(value + ORIGIN_TO_DATE_SHIFT);
  const afterOriginY = (value) => afterHeaderY(value + ORIGIN_HEIGHT_REDUCTION + ORIGIN_TO_DATE_SHIFT);
  const ENCOMIENDA_Y_SHIFT = destinationBaseY - encomiendaTopY - 4;
  const SUMMARY_Y_SHIFT = dynamicSummaryShift + ENCOMIENDA_Y_SHIFT - 31;
  const encomiendaY = (value) => afterOriginY(value + ENCOMIENDA_Y_SHIFT);
  const summaryY = (value) => afterOriginY(value + SUMMARY_Y_SHIFT);
  const issuerAddress = empresa.domicilio_fiscal || empresa.direccion || empresa.direccion_fiscal || empresa.domicilio || empresa.direccion_completa || "";
  const mainValueX = M + 41;
  const personValueX = M + 47;

  wrap(empresa.razon_social || empresa.nombre_comercial || "TRANSPORTE DE ENCOMIENDAS", semibold, 8.6, CW, 2)
    .forEach((item, index) => centered(page, item, bodyY(576 - (index * 5.8)), 8.6, semibold));
  centered(page, `RUC ${empresa.ruc || ""}`, bodyY(563), 10.8, regular);
  wrap(issuerAddress, regular, 7.4, CW, 2)
    .forEach((item, index) => centered(page, item, bodyY(550 - (index * 8.2)), 7.4, regular, MUTED));

  centered(page, documentName(code), afterHeaderY(514), 8.8, regular);
  text(page, "CPE", M + 8, afterHeaderY(496), 8.1, semibold, MUTED, 24);
  mixedLine(page, [
    { value: displayNumber || "MODELO", size: 16.8, font: regular },
    { value: datePe(issueDate), size: 9.2, font: regular, gap: 4 },
    { value: timePe(issueTime), size: 8.8, font: regular, gap: 4 },
  ], mainValueX, afterHeaderY(496), W - M);
  dotted(page, afterHeaderY(487));

  drawIcon(page, ICONS.place, M - 5, originY(448), 14, ICON_MUTED);
  text(page, "ORIGEN", M + 8, originY(440), 8.1, semibold, MUTED, 34);
  text(page, String(origin).toUpperCase(), mainValueX, originY(439.6), 13.2, regular, INK, W - M - mainValueX);
  text(page, "REMITENTE:", M + 8, originY(429), originBlockFontSize, semibold, MUTED, 41);
  text(page, senderName, personValueX, originY(428.4), originBlockDataFontSize, regular, INK, W - M - personValueX);
  text(page, "DOC:", M + 8, originY(417.2), originBlockFontSize, semibold, MUTED, 20);
  text(page, senderDoc, personValueX, originY(416.2), originBlockDataFontSize, regular, INK, 50);
  text(page, "TEL:", M + 109, originY(417.2), originBlockFontSize, semibold, MUTED, 20);
  text(page, encomienda.cliente_telefono || "-", M + 132, originY(416.2), originBlockDataFontSize, regular, INK, 61);
  senderZoneLines.forEach((item, index) => {
    const y = originOptionalStartY - (index * originOptionalLineHeight);
    text(page, index === 0 ? "ZONA:" : "", M + 8, originY(y), originBlockFontSize, semibold, MUTED, 54);
    text(page, item, personValueX, originY(y - 0.4), originBlockDataFontSize, regular, INK, W - M - personValueX);
  });
  senderBillingAddressLines.forEach((item, index) => {
    const y = originOptionalStartY - ((senderZoneLines.length + index) * originOptionalLineHeight);
    text(page, index === 0 ? "DIR FACT:" : "", M + 8, originY(y), originBlockFontSize, semibold, MUTED, 54);
    text(page, item, personValueX, originY(y - 0.4), originBlockDataFontSize, regular, INK, W - M - personValueX);
  });
  senderPickupAddressLines.forEach((item, index) => {
    const y = originOptionalStartY - ((senderZoneLines.length + senderBillingAddressLines.length + index) * originOptionalLineHeight);
    text(page, index === 0 ? "DIR RECOJO:" : "", M + 8, originY(y), originBlockFontSize, semibold, MUTED, 54);
    text(page, item, personValueX, originY(y - 0.4), originBlockDataFontSize, regular, INK, W - M - personValueX);
  });
  dotted(page, afterOriginY(356));

  drawIcon(page, ICONS.place, M - 5, afterOriginY(353), 14, ICON_MUTED);
  text(page, "DEST.", M + 8, afterOriginY(341.6), 8.1, semibold, MUTED, 34);
  text(page, String(destination).toUpperCase(), mainValueX, afterOriginY(341.6), 13.2, regular, INK, W - M - mainValueX);
  text(page, "DESTIN.:", M + 8, afterOriginY(330.6), originBlockFontSize, semibold, MUTED, 41);
  text(page, receiverName, personValueX, afterOriginY(330), originBlockDataFontSize, regular, INK, W - M - personValueX);
  text(page, "DNI:", M + 8, afterOriginY(319.2), originBlockFontSize, semibold, MUTED, 20);
  text(page, receiverDoc, personValueX, afterOriginY(318.6), originBlockDataFontSize, regular, INK, 50);
  text(page, "TEL:", M + 109, afterOriginY(319.2), originBlockFontSize, semibold, MUTED, 20);
  text(page, encomienda.destinatario_telefono || "-", M + 132, afterOriginY(318.6), originBlockDataFontSize, regular, INK, 61);
  receiverZoneLines.forEach((item, index) => {
    const y = destinationOptionalStartY - (index * destinationOptionalLineHeight);
    text(page, index === 0 ? "ZONA:" : "", M + 8, afterOriginY(y), originBlockFontSize, semibold, MUTED, 54);
    text(page, item, personValueX, afterOriginY(y - 0.4), originBlockDataFontSize, regular, INK, W - M - personValueX);
  });
  receiverAddressLines.forEach((item, index) => {
    const y = destinationOptionalStartY - ((receiverZoneLines.length + index) * destinationOptionalLineHeight);
    text(page, index === 0 ? "DIR LLEGADA:" : "", M + 8, afterOriginY(y), originBlockFontSize, semibold, MUTED, 54);
    text(page, item, personValueX, afterOriginY(y - 0.4), originBlockDataFontSize, regular, INK, W - M - personValueX);
  });
  dotted(page, afterOriginY(destinationBaseY - 2));

  drawIcon(page, ICONS.package, M - 5, encomiendaY(258), 14, ICON_MUTED);
  text(page, "ENCOMIENDA", M + 8, encomiendaY(249), 9.2, regular, MUTED, 58);
  descriptionLines.forEach((item, index) => {
    text(page, item, M + 8, encomiendaY(232 - (index * descriptionLineHeight)), descriptionFontSize, regular, INK, CW - 16);
  });

  box(page, M, summaryY(114), CW, 62, WHITE, LIGHT_LINE, 0.75);
  page.drawImage(qrImage, { x: M + 8, y: summaryY(119), width: 53, height: 53 });
  page.drawLine({ start: { x: 75, y: summaryY(118) }, end: { x: 75, y: summaryY(173) }, thickness: 0.45, color: LIGHT_LINE, dashArray: [2, 3] });
  page.drawLine({ start: { x: 136, y: summaryY(118) }, end: { x: 136, y: summaryY(173) }, thickness: 0.45, color: LIGHT_LINE, dashArray: [2, 3] });
  drawPaymentStatusLarge(page, {
    isPending: isPaymentPending,
    label: paymentLabel,
    x: 77,
    y: summaryY(150),
    width: 58,
    bold,
    semibold,
  });
  centeredIn(page, "TOTAL", W - M - 11 - 67, summaryY(164), 67, 8, regular);
  centeredIn(page, "S/", W - M - 11 - 67, summaryY(149), 67, 9.8, regular);
  right(page, money(total), summaryY(127), 21, regular, INK, W - M - 11, 67);

  text(page, "TERMINOS Y CONDICIONES", M, summaryY(99), 8.2, semibold);
  const termFontSize = 8.5;
  const termLineHeight = 9.5;
  let termsCursor = 89;
  const drawTerms = (value, { font = regular, color = MUTED, gap = 1.8, maxLines = 8 } = {}) => {
    wrap(String(value || "").toUpperCase(), font, termFontSize, CW, maxLines).forEach((item) => {
      text(page, item, M, summaryY(termsCursor), termFontSize, font, color, CW);
      termsCursor -= termLineHeight;
    });
    termsCursor -= gap;
  };

  drawTerms("EL RECOJO ES PERSONAL, CON DNI.", { font: semibold, color: INK, gap: 1.2, maxLines: 1 });
  drawTerms("TIEMPO DE RECOJO MAXIMO 05 DIAS. PASADO ESTE PLAZO SE COBRARA UN COSTO ADICIONAL POR ALMACENAMIENTO.", { maxLines: 3 });
  drawTerms("NOTA: LA EMPRESA NO SE RESPONSABILIZA DE ENCOMIENDAS EN LOS SIGUIENTES PUNTOS:", { font: semibold, color: INK, gap: 1.2, maxLines: 2 });
  [
    "1. No se responsabiliza por perecibles, recipientes liquidos, dinero o bienes no declarados, vida de animales y mercancia mal embalada.",
    "2. La empresa no se responsabiliza si no se declara lo que se envia.",
    "3. Si declara valor, el usuario pagara 5% de dicho valor mas el flete. Solo en este caso la indemnizacion sera al 100% del valor declarado, previa justificacion de preexistencia.",
    "4. El servicio de envio y entrega esta sujeto a la demanda y operatividad diaria.",
    "5. En caso de perdida, la empresa abonara conforme a ley.",
  ].forEach((item) => drawTerms(item, { gap: 0.8, maxLines: 4 }));

  // Ultimo texto del ticket. Se anota su y porque el recorte del papel en blanco
  // se calcula desde ahi: summaryY() ya descuenta las lineas que agrego cada
  // bloque, asi que el valor sube o baja con el contenido real y el recorte
  // nunca se lleva informacion.
  const horaLlegadaY = summaryY(termsCursor - 2);
  const ultimaLineaY = summaryY(termsCursor - 12);

  centered(page, `HORA LLEGADA: ${arrivalApprox || "-"}`, horaLlegadaY, 8.8, semibold, INK, CW);
  centered(page, `REGISTRADO POR: ${registeredBy || "-"}`, ultimaLineaY, 6.8, regular, MUTED, CW);

  // Se recorta solo la cola de papel en blanco (mas el margen de aire) y se
  // devuelve una pagina mas corta. El modal apila este ticket arriba del del
  // paquete sin recortarlo.
  const tintaInferior = ultimaLineaY - 2;
  const papelSinUso = Math.max(0, tintaInferior - TICKET_TRAILING_MARGIN);

  if (papelSinUso < H - 120) {
    // OJO: el recorte se hace dentro del MISMO documento. Con un PDFDocument
    // nuevo, pdf-lib deja la pagina embebida apuntando a las fuentes del
    // documento de origen (/BarlowCondensed) y esas referencias no existen en el
    // destino: el ticket salia con caracteres desconocidos.
    const embed = await pdfDoc.embedPage(page, {
      left: 0,
      bottom: papelSinUso,
      right: W,
      top: H,
    });
    const recortada = pdfDoc.addPage([W, H - papelSinUso]);
    recortada.drawPage(embed, { x: 0, y: 0, width: W, height: H - papelSinUso });
    // La pagina original queda en la posicion 0, que es la que lee el modal al
    // unir los dos tickets, asi que se elimina.
    pdfDoc.removePage(0);
    return pdfDoc.save();
  }

  return pdfDoc.save();
};

export default async function crearTicketEncomiendaTributarioPdf({ encomienda = {}, empresa = {} }) {
  const jsonTicket = ticketPayloadDesdeFormulario({ encomienda, empresa });
  const pdfBytes = await generarPdfTicketEncomiendaTributario(jsonTicket);
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  return URL.createObjectURL(blob);
}
